import { z } from 'zod';

import { type FilterChip } from '@apps/web/components/ui/filter_bar';
import { type VoiceCallAuditStatus } from '@libraries/typescript/api_client';

export const PER_PAGE = 50;

export const PIPELINE_OPTIONS = ['retell', 'inhouse'] as const;
export const CALL_TYPE_OPTIONS = [
    'callout',
    'recurring_shift',
    'clock_in',
    'clock_out',
    'unknown',
] as const;
export const CALL_DIRECTION_OPTIONS = [
    'inbound',
    'outbound',
    'inbound_ringcentral',
] as const;
export const RATING_OPTIONS = [
    'manual_bad',
    'agent_perfect',
    'agent_good',
    'agent_bad',
    'internal_perfect',
    'internal_good',
    'internal_bad',
    'user_perfect',
    'user_good',
    'user_bad',
] as const;

type VoiceQaRatingFilter = (typeof RATING_OPTIONS)[number];
type VoiceQaApiRatingFilter = Exclude<VoiceQaRatingFilter, 'manual_bad'>;

/**
 * Expands the UI-only manual option into both human rating sources. Empty
 * selections stay undefined so the API query omits the rating parameter.
 */
export function expandRatingFiltersForApi(
    ratings: ReadonlyArray<VoiceQaRatingFilter> | undefined,
): Array<VoiceQaApiRatingFilter> | undefined {
    if (!ratings?.length) {
        return undefined;
    }

    return Array.from(
        new Set(
            ratings.flatMap((rating) =>
                rating === 'manual_bad'
                    ? (['internal_bad', 'user_bad'] as const)
                    : [rating],
            ),
        ),
    );
}
export const AUDIT_STATUS_QUERY_OPTIONS = [
    'pending',
    'completed',
    'needs_review',
    'failed',
] as const satisfies ReadonlyArray<VoiceCallAuditStatus>;
export const ACTIVITY_EVENT_KIND_OPTIONS = [
    'voice_call_note',
    'callout_opened',
    'callout_closed',
    'callout_response',
    'missed_clock_in',
    'missed_clock_out',
    'after_hours_receptionist',
] as const;
export const SEVERITY_OPTIONS = ['low', 'medium', 'high'] as const;
// Single-value dimensions backed by the boolean API params
// (`has_proposed_bucket` / `transferred_to_back_office`); a present chip value
// flips the corresponding flag on.
export const PROPOSAL_STATUS_OPTIONS = ['has_proposal'] as const;
export const TRANSFER_STATUS_OPTIONS = ['transferred'] as const;
// Manual-review sources, mapped by the backend `manual_review` list param
// (customer_review → user rating, internal_review → internal rating).
export const MANUAL_REVIEW_OPTIONS = [
    'customer_review',
    'internal_review',
] as const;

const arrayParam = <T extends z.ZodTypeAny>(schema: T) =>
    z
        .preprocess((value) => {
            if (value === undefined || value === null) {
                return undefined;
            }
            if (Array.isArray(value)) {
                return value;
            }
            if (typeof value === 'string') {
                if (!value.trim()) {
                    return [];
                }
                return value
                    .split(',')
                    .map((item) => item.trim())
                    .filter(Boolean);
            }
            return value;
        }, z.array(schema))
        .optional();

export const booleanParam = z
    .preprocess((value) => {
        if (value === undefined || value === null || value === '') {
            return undefined;
        }
        if (typeof value === 'boolean') {
            return value;
        }
        if (typeof value === 'string') {
            if (value === 'true') {
                return true;
            }
            if (value === 'false') {
                return false;
            }
        }
        return value;
    }, z.boolean())
    .optional();

export const voiceQaSearchSchema = z.object({
    page: z.coerce.number().optional().default(1),
    organization_name: arrayParam(z.string()),
    pipeline: arrayParam(z.enum(PIPELINE_OPTIONS)),
    call_direction: arrayParam(z.enum(CALL_DIRECTION_OPTIONS)),
    call_type: arrayParam(z.enum(CALL_TYPE_OPTIONS)),
    rating: arrayParam(z.enum(RATING_OPTIONS)),
    bucket: arrayParam(z.string()),
    audit_status: arrayParam(z.enum(AUDIT_STATUS_QUERY_OPTIONS)),
    audit_finding_bucket: arrayParam(z.string()),
    audit_finding_severity: arrayParam(z.enum(SEVERITY_OPTIONS)),
    activity_event_kind: arrayParam(z.enum(ACTIVITY_EVENT_KIND_OPTIONS)),
    proposal_status: arrayParam(z.enum(PROPOSAL_STATUS_OPTIONS)),
    transfer_status: arrayParam(z.enum(TRANSFER_STATUS_OPTIONS)),
    manual_review: arrayParam(z.enum(MANUAL_REVIEW_OPTIONS)),
    created_after: z.string().optional(),
    created_before: z.string().optional(),
    search: z.string().optional(),
    sort_by: z
        .enum(['created_at', 'organization_name', 'duration_ms'])
        .optional()
        .default('created_at'),
    sort_order: z.enum(['asc', 'desc']).optional().default('desc'),
});

export type VoiceQaSearch = z.infer<typeof voiceQaSearchSchema>;

export const shadowLaneSearchSchema = z.object({
    shadow_model: z.string().optional(),
    shadow_reasoning_effort: z.string().optional(),
});

export type ShadowLaneSearch = z.infer<typeof shadowLaneSearchSchema>;

/**
 * The detail view inherits every list filter/sort/page param so it can rebuild
 * the exact list it was opened from. `from_list` is set only when navigating
 * from the list; its absence (a shared link, bookmark, or refresh without the
 * params) is the signal that there is no list context to navigate.
 */
export const voiceQaDetailSearchSchema = voiceQaSearchSchema.extend({
    from_list: z.boolean().optional(),
});

export type VoiceQaDetailSearch = z.infer<typeof voiceQaDetailSearchSchema>;

/** Strip the detail-only `from_list` flag back down to list search params, for
 * returning to the list with the same filters intact. */
export function voiceDetailSearchToListSearch(
    search: VoiceQaDetailSearch,
): VoiceQaSearch {
    const { from_list: _fromList, ...listSearch } = search;
    return listSearch;
}

export type VoiceQaFilterDimensionKey =
    | 'organization'
    | 'pipeline'
    | 'call_direction'
    | 'call_type'
    | 'rating'
    | 'qa_status'
    | 'audit_finding_bucket'
    | 'audit_finding_severity'
    | 'agent_bucket'
    | 'activity_event_kind'
    | 'proposal_status'
    | 'transfer_status'
    | 'manual_review';

export type VoiceQaFilterChip = FilterChip<VoiceQaFilterDimensionKey>;

/**
 * The subset of search params owned by the filter bar. Voice dimensions only
 * offer the `is` operator and the backend accepts INCLUDE-style params only,
 * so there are no `exclude_*` / `all_*` counterparts.
 */
export type VoiceQaFilterParams = {
    readonly organization_name: VoiceQaSearch['organization_name'];
    readonly pipeline: VoiceQaSearch['pipeline'];
    readonly call_direction: VoiceQaSearch['call_direction'];
    readonly call_type: VoiceQaSearch['call_type'];
    readonly rating: VoiceQaSearch['rating'];
    readonly audit_status: VoiceQaSearch['audit_status'];
    readonly audit_finding_bucket: VoiceQaSearch['audit_finding_bucket'];
    readonly audit_finding_severity: VoiceQaSearch['audit_finding_severity'];
    readonly bucket: VoiceQaSearch['bucket'];
    readonly activity_event_kind: VoiceQaSearch['activity_event_kind'];
    readonly proposal_status: VoiceQaSearch['proposal_status'];
    readonly transfer_status: VoiceQaSearch['transfer_status'];
    readonly manual_review: VoiceQaSearch['manual_review'];
};

/**
 * Maps URL search params to filter-bar chips. The order here is the stable
 * render order of chips in the bar.
 */
export function searchToFilterChips(
    search: VoiceQaSearch,
): Array<VoiceQaFilterChip> {
    const chips: Array<VoiceQaFilterChip> = [];
    // Voice dimensions are `is`-only; every chip carries the include operator.
    const pushChip = (
        dimension: VoiceQaFilterDimensionKey,
        values: Array<string> | undefined,
    ) => {
        const filteredValues = (values ?? [])
            .map((value) => value.trim())
            .filter(Boolean);
        if (filteredValues.length > 0) {
            chips.push({ dimension, operator: 'is', values: filteredValues });
        }
    };
    pushChip('organization', search.organization_name);
    pushChip('pipeline', search.pipeline);
    pushChip('call_direction', search.call_direction);
    pushChip('call_type', search.call_type);
    pushChip('rating', search.rating);
    pushChip('qa_status', search.audit_status);
    pushChip('audit_finding_bucket', search.audit_finding_bucket);
    pushChip('audit_finding_severity', search.audit_finding_severity);
    pushChip('agent_bucket', search.bucket);
    pushChip('activity_event_kind', search.activity_event_kind);
    pushChip('proposal_status', search.proposal_status);
    pushChip('transfer_status', search.transfer_status);
    pushChip('manual_review', search.manual_review);
    return chips;
}

/**
 * Inverse of searchToFilterChips. Returns every filter-owned param so that
 * dimensions absent from the chips are explicitly cleared from the URL.
 */
export function filterChipsToSearchParams(
    chips: Array<VoiceQaFilterChip>,
): VoiceQaFilterParams {
    const params: {
        -readonly [K in keyof VoiceQaFilterParams]: VoiceQaFilterParams[K];
    } = {
        organization_name: undefined,
        pipeline: undefined,
        call_direction: undefined,
        call_type: undefined,
        rating: undefined,
        audit_status: undefined,
        audit_finding_bucket: undefined,
        audit_finding_severity: undefined,
        bucket: undefined,
        activity_event_kind: undefined,
        proposal_status: undefined,
        transfer_status: undefined,
        manual_review: undefined,
    };
    const assignValues = <T extends string>(
        values: Array<string>,
        allowedValues: ReadonlyArray<T> | null,
    ): Array<T> | Array<string> | undefined => {
        const filteredValues = values
            .map((value) => value.trim())
            .filter(Boolean);
        if (allowedValues === null) {
            return filteredValues.length > 0 ? filteredValues : undefined;
        }
        const allowed = new Set<string>(allowedValues);
        const validValues = filteredValues.filter((value): value is T =>
            allowed.has(value),
        );
        return validValues.length > 0 ? validValues : undefined;
    };
    // Voice dimensions are `is`-only, so every chip maps to the single
    // INCLUDE param for its dimension; there is no exclude/all counterpart.
    for (const chip of chips) {
        if (chip.values.length === 0) {
            continue;
        }
        switch (chip.dimension) {
            case 'organization':
                params.organization_name = assignValues(chip.values, null) as
                    | Array<string>
                    | undefined;
                break;
            case 'pipeline':
                params.pipeline = assignValues(
                    chip.values,
                    PIPELINE_OPTIONS,
                ) as VoiceQaFilterParams['pipeline'];
                break;
            case 'call_direction':
                params.call_direction = assignValues(
                    chip.values,
                    CALL_DIRECTION_OPTIONS,
                ) as VoiceQaFilterParams['call_direction'];
                break;
            case 'call_type':
                params.call_type = assignValues(
                    chip.values,
                    CALL_TYPE_OPTIONS,
                ) as VoiceQaFilterParams['call_type'];
                break;
            case 'rating':
                params.rating = assignValues(
                    chip.values,
                    RATING_OPTIONS,
                ) as VoiceQaFilterParams['rating'];
                break;
            case 'qa_status':
                params.audit_status = assignValues(
                    chip.values,
                    AUDIT_STATUS_QUERY_OPTIONS,
                ) as VoiceQaFilterParams['audit_status'];
                break;
            case 'audit_finding_bucket':
                params.audit_finding_bucket = assignValues(
                    chip.values,
                    null,
                ) as Array<string> | undefined;
                break;
            case 'audit_finding_severity':
                params.audit_finding_severity = assignValues(
                    chip.values,
                    SEVERITY_OPTIONS,
                ) as VoiceQaFilterParams['audit_finding_severity'];
                break;
            case 'agent_bucket':
                params.bucket = assignValues(chip.values, null) as
                    | Array<string>
                    | undefined;
                break;
            case 'activity_event_kind':
                params.activity_event_kind = assignValues(
                    chip.values,
                    ACTIVITY_EVENT_KIND_OPTIONS,
                ) as VoiceQaFilterParams['activity_event_kind'];
                break;
            case 'proposal_status':
                params.proposal_status = assignValues(
                    chip.values,
                    PROPOSAL_STATUS_OPTIONS,
                ) as VoiceQaFilterParams['proposal_status'];
                break;
            case 'transfer_status':
                params.transfer_status = assignValues(
                    chip.values,
                    TRANSFER_STATUS_OPTIONS,
                ) as VoiceQaFilterParams['transfer_status'];
                break;
            case 'manual_review':
                params.manual_review = assignValues(
                    chip.values,
                    MANUAL_REVIEW_OPTIONS,
                ) as VoiceQaFilterParams['manual_review'];
                break;
            default: {
                const exhaustive: never = chip.dimension;
                throw new Error(`Unhandled filter dimension: ${exhaustive}`);
            }
        }
    }
    return params;
}
